import React, { useState, useEffect, useRef } from 'react';
import Ribbon from '../../../common/Toast';
import QueueHeader from './QueueViewHeader';
import { format, differenceInMinutes } from 'date-fns';
import { useParams } from 'react-router-dom';

import styles from './QueueMonitor.module.scss';
import SidePanel from './QueueSidePanel';
import Token from './Token';
import { formatTokenNumber } from '../../../../utils/textOperations';
import {
  dbQueueWithCurDateRef,
} from '../../../../api/queue';
import {
  ref,
  get,
  update,
  onValue,
} from 'firebase/database';
import { getCurDate } from '../Dashboard/Dashboard_components/SelectDateRange';
import { db } from '../../../../FirebaseConfig';
import {
  pushNotification,
  sendSMSNotification,
} from '../../../../api/notify';
import Paging from '../../../common/Pagination/Paging';
import CalculateAverageWaitTime from '../../../../utils/QueueCalculation/CalculateAverageWaitTime' 
import { getUsersOrganizations } from '../../../../api/organization';
import { auth } from '../../../../FirebaseConfig';
import Toast from '../../../common/Toast';
import ModalNotifyForm from '../../../common/ModalNotifyForm/ModalNotifyForm';
import EmailNotification from '../../../../utils/EmailNotification/EmailNotification';


const PAGE_SIZE = 8;
const EmptyList = () => {
  return (
    <>
      <p style={{ textAlign:'center' }}>Your queue is currently empty.</p>
    </>
  );
};

const QueueMonitorView = (props) => {
  const { ...params } = useParams();
  const NotificationModalRef = useRef(null);

  const [queueId, setQueueId] = useState(params.queueId || null);
  const [queueInfo, setQueueInfo] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [isRefresh, setIsRefresh] = useState(false);
  const [servingNumber, setServingNumber] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [organizations, setOrganizations] = useState([]);
  const [userId, setUserId] = useState(null);
  
  useEffect(() => {
    setQueueId(params.queueId);
  }, [params]);

  //UserId

  useEffect(() => {
    // Listen to the authentication state
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  //Fetch org
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      getUsersOrganizations(currentUser.uid)
        .then((userOrganizations) => {
          setOrganizations(userOrganizations);
        })
        .catch((error) => {
          console.error('Error fetching organizations:', error);
        });
    }
  }, [userId]);

  //Get Queue basic Info
  useEffect(() => {
    try {
      if (queueId) {
        get(ref(db, `queues/${queueId}`))
          .then((snapshot) => {
            if (snapshot.exists()) {
              const queueInfo = snapshot.val();
              const basicInfo = {
                name: queueInfo.name,
                limit: queueInfo.limit,
                queueId: queueInfo.id,
                status: queueInfo.status,
                organization: organizations.find(
                  (org) => org.id === queueInfo.organizationId
                ),
                daily_stats: queueInfo.daily_stats,
              };
              setQueueInfo((prev) => ({ ...prev, ...basicInfo }));
            }
          })
          .catch((err) => {
            console.error('\tError with ', err.message);
          });
      } else {
        setQueueId(null);
        setQueueInfo(null);
      }
    } catch (err) {
      console.error('\tError with ', err.message);
    }
  }, [queueId, isRefresh, organizations]);

  // Trigger on tokens, queue status
  useEffect(() => {
    if (queueId) {
      const curDate = getCurDate();
      const queueRef = ref(
        db,
        `queues/${queueId}/daily_stats/${curDate}/guests`
      );

      const unsubscribe = onValue(queueRef, (snapshot) => {
        let queueData = [];

        if (!snapshot.val()) {
        setQueueInfo((prev) => ({
          ...prev,
          totalGuests: 0,
          activeGuests: 0,
          daily_stats: null,
        }));
        };
        snapshot.forEach((childSnapshot) => {
          if (
            childSnapshot.val().status === 'waiting' ||
            childSnapshot.val().status === 'waiting-notified'
          ) {
            queueData.push({ key: childSnapshot.key, ...childSnapshot.val() });
          }
        });
        queueData.sort((a, b) => a.position - b.position);
        setTokens(queueData);
        setQueueInfo((prev) => ({
          ...prev,
          totalGuests: snapshot.size,
          activeGuests: queueData.length,
          daily_stats: {
            [new Date().toLocaleDateString('en-CA')]: {
              guests: {
                ...snapshot.val(),
              },
            },
          },
        }));
        setCurrentPage(1);
      });

      //Listener for queue status
      const unsubscribeStatus = onValue(
        ref(db, `queues/${queueId}/status`),
        (snapshot) => {
          if (snapshot) {
            setQueueInfo((prev) => ({ ...prev, status: snapshot.val() }));
          }
        }
      );

      return () => {
        unsubscribe();
        unsubscribeStatus();
      }; // Clean up the listener on component unmount
    }
  }, [queueId]);

  const handleNoShowUpGuest = async (guestToken) => {
    if (tokens.length === 1) return;
    try {
      const curDate = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
      const newPosition = tokens.length;
      const updatePositions = {};
      tokens.forEach((t) => {
        if (t.id === guestToken.id) {
          updatePositions[`/guests/${t.id}/position`] = newPosition;
          updatePositions[`/guests/${t.id}/status`] = 'waiting';
          updatePositions[`/guests/${t.id}/updatedAt`] = curDate;
        } else if (t.position > guestToken.position) {
          //update new posiotn list for guest in queue
          updatePositions[`/guests/${t.id}/position`] = t.position - 1;
          updatePositions[`/guests/${t.id}/updatedAt`] = curDate;
        }
      });
      await update(dbQueueWithCurDateRef(queueId), updatePositions);
    } catch (error) {
      console.error('Error handling no-show guest:', error);
    }
  };

  const handleCheckInGuest = async (guestToken) => {
    try {
      const startedTime = new Date(guestToken.startedTime);
      //servedTime
      const endTime = new Date();
      const waitTime = differenceInMinutes(endTime, startedTime);

      const updateGuestTokenPositions = {};
      updateGuestTokenPositions[`/guests/${guestToken.id}/position`] = 0;
      updateGuestTokenPositions[`/guests/${guestToken.id}/endTime`] = endTime;
      updateGuestTokenPositions[`/guests/${guestToken.id}/waitTime`] = waitTime;
      updateGuestTokenPositions[`/guests/${guestToken.id}/status`] = 'served';
      const curDate = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
      updateGuestTokenPositions[`/guests/${guestToken.id}/updatedAt`] = curDate;

      //Update info for guest token that is just served
      await update(
        dbQueueWithCurDateRef(guestToken.queueId),
        updateGuestTokenPositions
      );

      await CalculateAverageWaitTime(guestToken);

      const updatePositions = {};
      tokens.forEach((t) => {
        if (t.position > guestToken.position - 1) {
          //update new posiotn list for guest in queue
          updatePositions[`/guests/${t.id}/position`] = t.position - 1;
          updatePositions[`/guests/${t.id}/updatedAt`] = curDate;
        }
      });
      await update(dbQueueWithCurDateRef(guestToken.queueId), updatePositions);
      setServingNumber(formatTokenNumber(guestToken.number));
    } catch (error) {
      console.error('\tError by: ', error.message);
    }
  };

  const handleCancelGuest = async (guestToken) => {
    try {
      const activeRef = dbQueueWithCurDateRef(guestToken.queueId, '/guests');
      get(activeRef).then(async (snapshot) => {
        const updatePositions = {};
        updatePositions[`/${guestToken.id}/status`] = 'canceled';

        const curDate = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
        updatePositions[`/${guestToken.id}/updatedAt`] = curDate;

        const updatedList = Object.values(snapshot.val()).filter((value) =>
          value.status.startsWith('waiting')
        );
        updatedList.forEach((t) => {
          if (t.position > guestToken.position) {
            //update new posiotn list for guest in queue
            updatePositions[`/${t.id}/position`] = t.position - 1;
            updatePositions[`/${t.id}/updatedAt`] = curDate;
          }
        });
        await update(activeRef, updatePositions);
      });
    } catch (error) {
      console.error('\tError by: ', error.message);
    }
  };

  const handleNotifyGuest = async (formData) => {
    const guestToken = formData.token;
    try {
      const dbRef = dbQueueWithCurDateRef(
        formData.token.queueId,
        `/guests/${guestToken.id}`
      );
      const updateList = {};
      updateList['status'] = 'waiting-notified';
      const curDate = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
      updateList[`updatedAt`] = curDate;
      updateList['notifyMessage'] = formData.message;

      await update(dbRef, updateList);
      EmailNotification({
        guestName: guestToken.name,
        guestEmail: guestToken.email,
        //Please modify the below values after the form is implemented 
        subjectLine: formData.subject || 'Queue Update',
        fromName: formData.from || 'Queue Me Up team',
        message: formData.message || 'message was not added, check with admin/volunteer',
      });
  
//Commented out pushNotification and sendSMSNotification as it was previously implemented but is no longer functioning as expected. Due to time constraints, we weren't able to investigate the issue thoroughly. It has been commented out for now, with plans to revisit and investigate further later.   
/*       if (guestToken.notifyTokenId) pushNotification(guestToken.notifyTokenId);
      sendSMSNotification(queueInfo.name, guestToken.phone, guestToken.name); */

    } catch (error) {
      console.error('\tError in sending notification: ', error.message);
    }
  };

  const handleAddGuest = async (guestToken) => {
    try {
      if (queueInfo.status === 'Paused') {
        alert("The queue is paused, can't add a new guest.");
        return;
      }
      if (queueInfo.activeGuests >= Number(queueInfo.limit)) {
        alert('The queue is full, please join later.');
        return;
      }
      guestToken.position = queueInfo.activeGuests + 1;
      guestToken.number = queueInfo.totalGuests + 1;
      const curDate = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
      guestToken.updatedAt = curDate;
      const dbRef = dbQueueWithCurDateRef(queueId, '/guests');

      //add to DB
      const updateList = {};
      updateList[`/${guestToken.id}`] = guestToken;
      await update(dbRef, updateList);
      EmailNotification({
        guestName: guestToken.name,
        guestEmail: guestToken.email,
        subjectLine: 'You are added to the queue',
        fromName: 'Queue Me Up team',
        message: `Hi, you can monitor your progress here: http://localhost:3000/join/token?guestId=${guestToken.id}&queueId=${guestToken.queueId}`,
      });
    } catch (err) {
      console.error('\tError: ', err.message);
    }
  };

  if(!userId){
    return(
      <Toast
      title="Temporary warning!"
      subTitle="Please sign up to work on this page."
    />
    )
  }

  return (
    <>
      <ModalNotifyForm
        ref={NotificationModalRef}
        onConfirm={handleNotifyGuest}
      ></ModalNotifyForm>
      <div className={styles['queue-view']}>
        <QueueHeader
          title="Queue Management"
          onRefresh={setIsRefresh}
          onChangeQueue={setQueueId}
        />

        {queueInfo && (
          <div className={`${styles['main-body']}`}>
            <div className={` ${styles['side-admin-panel']}`}>
              <SidePanel queueInfo={queueInfo} onAddGuest={handleAddGuest} />
            </div>
            <div className={` ${styles['token-list-contaner']}`}>
              <div>
                {servingNumber && (
                  <div className={styles['serving-numer']}>
                    <Ribbon
                      className={styles['serving-numer']}
                      title="Serving"
                      subTitle={`Number ${servingNumber}`}
                    />
                  </div>
                )}
                <h3 className={styles['wait-list-title']}>Waiting List</h3>

                <div className={styles['token-list']} />

                {tokens.length === 0 ? (
                  <EmptyList />
                ) : (
                  tokens
                    .slice(
                      (currentPage - 1) * PAGE_SIZE,
                      currentPage * PAGE_SIZE
                    )
                    .map((token) => (
                      <Token
                        token={token}
                        key={token.id}
                        onNoShowUp={handleNoShowUpGuest}
                        onCheckIn={handleCheckInGuest}
                        onCancel={handleCancelGuest}
                        onNotify={(token) =>
                          NotificationModalRef.current.openModal({
                            from: queueInfo.name,
                            token
                          })
                        }
                      />
                    ))
                )}

                {tokens.length > 8 && (
                  <Paging
                    totalItems={tokens.length}
                    onSelectedPage={setCurrentPage}
                    currentPage={currentPage}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default QueueMonitorView;
